import { Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent, DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, SocketEventTypes } from '@app/enums';
import { AnalyticService, AuthService, FileHelperService, NotifyService, SocketService } from '@app/services';
import { IndexView } from '@common/views';
import { EmployeeDocumentsTable } from '@employees/services/tables/documents/employee-documents.table';
import { EmployeeDocumentForm } from '@forms/documents/components';
import { Role } from '@models/account/role.model';
import { EmployeeDocument } from '@models/documents/employee-document.model';
import { SignatureRequest } from '@models/documents/signature-request.model';
import { Employee } from '@models/employee/employee.model';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';
import { Subscription } from 'rxjs';

/**
 * As admin, go to Employee Profile > Documents, then you are here.
 */
@Component({
    templateUrl: './documents-index.template.html',
    styleUrls: ['./documents-index.style.scss'],
})
export class DocumentsIndexView extends IndexView implements OnDestroy {
    @ViewChild('createModal', { static: true }) createModal!: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmDeleteModal') confirmDeleteModal!: ConfirmationDialogComponent;

    viewingSelf = false;
    table: EmployeeDocumentsTable;
    isLoading = true;
    employee: Employee;
    roles: Role[] = [];
    canCreateDocument = false;

    /**
     * isIncompleteSignatureRequest informs the template if this document
     * is backed by a signature request that hasn't fully completed yet.
     */
    isIncompleteSignatureRequest = false;

    private tableSub: Subscription;
    private socketSub: Subscription;

    constructor(
        public fileHelper: FileHelperService,
        private auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private socket: SocketService,
        protected analyticService: AnalyticService
    ) {
        super();

        this.employee = this.route.snapshot.data.models.get('employee');
        this.viewingSelf = this.employee.is(this.auth.employee);
        this.table = new EmployeeDocumentsTable(this.auth, this.employee, 'documents-v2');
        this.getRoles();
        this.tableSub = this.table.onLoading.subscribe((isLoading) => (this.isLoading = isLoading));
        this.canCreateDocument = this.viewingSelf || this.auth.canAccessEmployee(this.employee);
        this.socketSub = this.socket
            .getEmployeeChannel(this.employee, SocketEventTypes.documentDownloaded)
            .subscribe(() => {
                this.table.reload();
            });
    }

    ngOnDestroy(): void {
        this.tableSub.unsubscribe();
        this.socketSub.unsubscribe();
    }

    addDocument(): void {
        this.router.navigate(['bulk-upload'], { relativeTo: this.route });
    }

    async onCreate(form: EmployeeDocumentForm): Promise<void> {
        const employeeDocument = form.employeeDocument;
        try {
            //  Fix TS compiler error: Object is possibly undefined
            // 'throw' of exception caught locally
            if (!form.file) {
                throw new Error('document.saveError');
            }

            const file = await this.fileHelper.store(form.file);

            employeeDocument.fileId = file.id;

            await employeeDocument
                .attach('roles', [...form.getSelectedRoles()])
                .param('employee', this.employee.id)
                .save();

            form.reset();
            this.table.reload();
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async onTableMenuEvent(event: MenuColumnEvent): Promise<void> {
        const eventType = event.menuItem.event;
        const document = event.data as EmployeeDocument;

        switch (eventType) {
            case 'download':
                {
                    const documentWithDownloadLink = await EmployeeDocument.param('employee', document.employeeId).find(
                        document.id
                    );
                    this.onDownload(documentWithDownloadLink);
                    this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentDownloaded);
                }
                break;
            case 'delete':
                {
                    this.isIncompleteSignatureRequest =
                        document.isSignatureRequest() && !(document.source as SignatureRequest).fileId;

                    const shouldDelete = await this.confirmDeleteModal.open();

                    if (!shouldDelete) {
                        return;
                    }

                    await document
                        .param('employee', document.employeeId)
                        .delete()
                        .then(() => {
                            this.notify.success('employees.documents.documentDeleted');
                            this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentDeleted);
                            this.table.reload();
                        })
                        .catch(() => {
                            this.notify.error('employees.documents.errorDeletingDocument');
                        });
                }
                break;
            case 'remind':
                await document.notify();
                this.notify.success('employees.documents.reminderSent');
                this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentRemind);
                break;
        }
    }

    onDownload(employeeDocument: EmployeeDocument): void {
        this.fileHelper.save(employeeDocument.file.id, employeeDocument.name, employeeDocument.file.type ?? '');
    }

    private getRoles(): void {
        Role.where('company_id', this.auth.company.id)
            .get()
            .then(([roles, _]: [Role[], unknown]) => {
                this.roles = roles;
            })
            .catch((err) => this.notify.error(err));
    }
}
